import {
  Maybe,
  ProductOfferTile,
  ProductOfferTileType,
  TileStatus,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { parseOptionalGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import { AccountIconType } from 'shared/components/vxl/AccountIcon/AccountIcon.helpers';
import { HorizontalAccountCardProps } from 'shared/components/vxl/cards/HorizontalAccountCard/HorizontalAccountCard';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';

interface ProductOfferTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD;
  props: HorizontalAccountCardProps;
}

const productOfferTile = (
  {
    id,
    status,
    title,
    description,
    productOfferTileType,
    clickAnalytics,
  }: ProductOfferTile,
  { productOfferTileClicked }: HomeCardDependencies,
): Maybe<ProductOfferTileNode> => {
  if (status !== TileStatus.Loaded) {
    return null;
  }

  const accountProductMap: Record<ProductOfferTileType, AccountIconType> = {
    [ProductOfferTileType.ChargeCard]: AccountIconType.ChargeCard,
    [ProductOfferTileType.SavingsAccount]: AccountIconType.Savings,
    [ProductOfferTileType.VaroAdvance]: AccountIconType.Advance,
    [ProductOfferTileType.PersonalLoan]: AccountIconType.Unknown,
  };

  const getAccountProduct = (
    product: ProductOfferTileType,
  ): AccountIconType => {
    return accountProductMap[product];
  };

  const event = parseOptionalGQLAnalyticsEvent(clickAnalytics);

  function getAnalyticsId(product: ProductOfferTileType) {
    switch (product) {
      case ProductOfferTileType.SavingsAccount:
        return 'savingsProductOfferCardClicked';
      case ProductOfferTileType.ChargeCard:
        return 'chargeCardProductOfferCardClicked';
      case ProductOfferTileType.VaroAdvance:
        return 'advanceProductOfferCardCardClicked';
    }
  }
  const getProduct = (product: ProductOfferTileType): string => {
    switch (product) {
      case ProductOfferTileType.VaroAdvance:
        return AccountIconType.Advance;
      case ProductOfferTileType.ChargeCard:
        return AccountIconType.ChargeCard;
      case ProductOfferTileType.SavingsAccount:
        return AccountIconType.Savings;
      default:
        return AccountIconType.Unknown;
    }
  };

  return {
    id: id,
    name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD,
    props: {
      titleText: title,
      titleAccessory: description,
      accountIconProps: {
        product: getAccountProduct(productOfferTileType),
      },
      amountText: '',
      onAddIconClick: () => {
        productOfferTileClicked?.(productOfferTileType, event);
      },
      onCardClick: () => {
        productOfferTileClicked?.(productOfferTileType, event);
      },
      analyticsId: getAnalyticsId(productOfferTileType),
      customAttributes: {
        name: event?.name || '',
        account_type: getProduct(productOfferTileType).toLowerCase(),
        ...event?.properties,
      },
    },
  };
};

export default productOfferTile;
